import AppSettings from "../../../../Settings/AppSettings";
import React, { useContext, useEffect, useState } from "react";
import { CategoryContext } from "../CategoryContext";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import EditCategoryAttributes from "./EditCategoryAttributes";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../../Context/AppContext";
import LoadingSuccessErrorAlert from "./LoadingSuccessErrorAlert";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Button from "Components/DesignSystem/Button/Button";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Refresh } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";

const ConstraintsItem = ({ label, value }) => {
  return (
    <div>
      <Typography variant="title" className="tw-text-ink">
        {`${label}:`}
      </Typography>
      <Typography variant="body-primary" className="tw-text-ink-tertiary">
        {`${value}`}
      </Typography>
    </div>
  );
};

/**
 * Category Attribute Constraints — on the design system since the category
 * module's Phase 5. Every service call, the LOV/INTEGER default coercion in
 * handle_save, the message-dependent 1000ms success delay, the permission gate
 * on Edit and the state flow are the legacy logic verbatim. Rendering changed:
 * DS Accordion (controlled, § 192 actions slot), and each constraint's detail
 * grid — a bootstrap `collapse` toggled by an empty chevron span, with the
 * item name a role="button" that did NOTHING on click — is now a nested DS
 * Accordion, so the name itself is the (keyboard-reachable) toggle.
 */
const CategoryAttributeConstraint = () => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const { editingItem } = useContext(CategoryContext);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const appData = useContext(AppContext);
  const [list, setList] = useState(null);
  const [EditConstraint, setEditConstraint] = useState(false);
  const [categoryAttributes, setCategoryAttributes] = useState(null);
  const [categoryId, setCategoryId] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [collapse, setCollapse] = useState(false);
  let [consAttribute, setConsAttribute] = useState(null);

  useEffect(() => {
    setConsAttribute(null);
    setLoading({
      status: "",
      message: "",
    });
  }, [editingItem.id]);

  useEffect(() => {
    if (list === null && collapse) {
      load_associated_Abs_attributes();
    }
  }, [collapse]);

  useEffect(() => {
    if (EditConstraint) {
      load_associated_Abs_attributes();
    }
  }, [EditConstraint]);

  async function load_associated_Abs_attributes(message) {
    setLoading({
      status: "Loading",
      message: `${
        message === undefined
          ? "Loading Category Attribute Constraints"
          : message
      }`,
    });
    let res = await ServiceWrapper.doGet(
      `${api_base_url}attribute/getconstraintsbycategoryid/${editingItem.id}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      setTimeout(
        () => {
          setLoading({ status: "Success", message: undefined });
        },
        message === undefined ? 0 : 1000
      );

      setConsAttribute(res.data.data);
      setList(res.data.data);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  async function handle_save() {
    setLoading({
      status: "Loading",
      message: "Saving updated Category Attributes",
    });
    categoryAttributes.validation_type = "LOV";
    if (categoryAttributes.validation_type === "") {
      categoryAttributes.validation_type = "LOV";
    }
    if (categoryAttributes.datatype === "") {
      categoryAttributes.datatype = "INTEGER";
    }
    let res = await ServiceWrapper.doPost(
      `${api_base_url}category/${editingItem.id}/attribute/${categoryId}/constraints/edit`,
      // object to be sent,
      categoryAttributes,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        setLoading({
          status: "Success",
          message: res.data.message,
        });
        load_associated_Abs_attributes(res.data.message);
      } else {
        setLoading({
          status: "Error",
          message: res.data.message,
        });
      }
    } else {
      setLoading({
        status: "Error",
        message: "Error while saving, Please try after some time.",
      });
    }
    setEditConstraint(false);
  }

  function handle_cancel() {
    setEditConstraint(false);
    setList(consAttribute);
  }

  /* Header-row actions (§ 192): the legacy floated these OVER the header with
     zIndex + marginTop:-34px; same render conditions, same handlers. Note the
     legacy Save has NO dirty gate here — kept that way. */
  const headerActions =
    collapse && loading.status === "Success" ? (
      !EditConstraint ? (
        <button
          aria-label="Refresh"
          data-testid="category-attribute-constraint-btn"
          type="button"
          className={`tw-flex tw-items-center tw-justify-center tw-p-1.5 tw-rounded-ctl tw-border-0 tw-bg-transparent tw-text-ink-secondary hover:tw-text-accent hover:tw-bg-surface-hover tw-cursor-pointer ${FOCUS_RING}`}
          onClick={() => {
            load_associated_Abs_attributes();
          }}
        >
          <Refresh size={16} aria-hidden="true" />
        </button>
      ) : (
        <>
          <Button
            variant="secondary"
            size="sm"
            data-testid="category-attribute-constraint-cancel-btn"
            onClick={() => {
              handle_cancel();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="category-attribute-constraint-save-btn"
            onClick={() => handle_save()}
          >
            Save
          </Button>
        </>
      )
    ) : undefined;

  return (
    <Accordion
      title={`${
        EditConstraint
          ? "Update " + categoryName + "  constraints"
          : "Category Attribute Constraints"
      }`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      actions={headerActions}
      data-testid="components-categoryattributeconstraint-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert
          loading={loading}
          data-testid="category-attribute-constraint-status"
        />
        {loading.status === "Success" && (
          <>
            {EditConstraint ? (
              <div className="tw-pt-3">
                <EditCategoryAttributes
                  categoryAttributes={categoryAttributes}
                  setCategoryAttributes={setCategoryAttributes}
                />
              </div>
            ) : (
              <div className="tw-pt-3">
                {consAttribute && consAttribute.length > 0 ? (
                  <div className="tw-space-y-2">
                    {consAttribute.map((item) => {
                      return (
                        <Accordion
                          key={item.id}
                          title={`Constraints: ${item.name}`}
                          data-testid={`components-categoryattributeconstraint-span-2-${item.id}`}
                          className="tw-border tw-border-edge tw-rounded-ctl tw-px-3"
                          actions={
                            <Button
                              variant="subtle"
                              size="sm"
                              data-testid={`category-constraint-${item.id}-edit-btn`}
                              onClick={() => {
                                if (
                                  appData.pm.checkPermission(
                                    "gmscategory_update"
                                  )
                                ) {
                                  setCategoryName(item.name);
                                  setCategoryId(item.id);
                                  setCategoryAttributes(
                                    item.attribute_constraints
                                  );
                                  setEditConstraint(true);
                                } else {
                                  toast.info(
                                    "You don't have permission to edit the category constraint"
                                  );
                                }
                              }}
                            >
                              Edit
                            </Button>
                          }
                        >
                          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-pb-3">
                            <ConstraintsItem
                              label="Validation "
                              value={
                                item?.attribute_constraints?.validation ?? false
                              }
                            />

                            <ConstraintsItem
                              label="Allowed Chars "
                              value={
                                item?.attribute_constraints?.allowed_chars ??
                                "-"
                              }
                            />

                            <ConstraintsItem
                              label="Allowed User Defined Values "
                              value={
                                item?.attribute_constraints
                                  ?.allowed_user_defined_values ?? false
                              }
                            />

                            <ConstraintsItem
                              label="Prohibited Chars "
                              value={
                                item?.attribute_constraints?.prohibited_chars ??
                                "-"
                              }
                            />

                            <ConstraintsItem
                              label="Attr Inheritance "
                              value={
                                item?.attribute_constraints?.attr_inheritance ??
                                false
                              }
                            />

                            <ConstraintsItem
                              label="Multi Select"
                              value={
                                item?.attribute_constraints?.multiselect ??
                                false
                              }
                            />

                            <ConstraintsItem
                              label="Child Attr "
                              value={
                                item?.attribute_constraints?.child_attr ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Multiselect Number "
                              value={`${
                                item.attribute_constraints
                                  .multiselect_number !== null
                                  ? item.attribute_constraints
                                      .multiselect_number
                                  : "-"
                              }`}
                            />

                            <ConstraintsItem
                              label="Datatype "
                              value={
                                item?.attribute_constraints?.datatype ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Multiselect Output Range "
                              value={
                                item?.attribute_constraints
                                  ?.multiselect_output_range ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Type "
                              value={
                                item?.attribute_constraints?.validation_type ??
                                "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Type Extra Criteria "
                              value={
                                item?.attribute_constraints
                                  ?.validation_type_extra_criteria ?? ""
                              }
                            />

                            <ConstraintsItem
                              label="Special Formatting "
                              value={
                                item?.attribute_constraints
                                  ?.special_formatting ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Extra Criteria Value 1 "
                              value={
                                item?.attribute_constraints
                                  ?.extra_criteria_value1 ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Range "
                              value={
                                item?.attribute_constraints?.validation_range ??
                                "-"
                              }
                            />

                            <ConstraintsItem
                              label="Extra Criteria Value 2 "
                              value={
                                item?.attribute_constraints
                                  ?.extra_criteria_value2 ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Error Alert Style "
                              value={
                                item?.attribute_constraints
                                  ?.validation_error_alert_style ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Input Message "
                              value={
                                item?.attribute_constraints
                                  ?.validation_input_message ?? false
                              }
                            />

                            <ConstraintsItem
                              label="Validation Error Alert "
                              value={
                                item?.attribute_constraints
                                  ?.validation_error_alert ?? false
                              }
                            />

                            <ConstraintsItem
                              label="Validation Error Alert Body "
                              value={
                                item?.attribute_constraints
                                  ?.validation_error_alert_body ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Error Alert Title "
                              value={
                                item?.attribute_constraints
                                  ?.validation_error_alert_title ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Input Message Title "
                              value={
                                item?.attribute_constraints
                                  ?.validation_input_message_title ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation Input Message Body "
                              value={
                                item?.attribute_constraints
                                  ?.validation_input_message_body ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Column Order "
                              value={
                                item?.attribute_constraints?.column_order ?? "-"
                              }
                            />

                            <ConstraintsItem
                              label="Validation "
                              value={
                                item?.attribute_constraints?.validation ?? false
                              }
                            />
                          </div>
                        </Accordion>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState
                    size="sm"
                    title="No categories attributes constraints found"
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Accordion>
  );
};

export default CategoryAttributeConstraint;
